import {
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { visitApi } from 'src/api/visit';
import { visitScopedPrescribingApi } from 'src/api/visit-scoped-prescribing';
import Iconify from 'src/components/iconify';
import { getFileIcon } from 'src/sections/@dashboard/profile/components/ProfileFilesTable';
import PatientNoteModal from 'src/sections/@dashboard/profile/components/PatientNoteModal/PatientNoteModal';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { getFileUrl } from 'src/utils/getFileUrl';
import {
  DocumentFileResponseDto,
  DocumentFileResponseDtoDocumentTypeEnum,
} from 'woofwoof-api';
import VisitModalEmptyState from './VisitModalEmptyState';
import { translateKey } from 'src/locales/translate';


interface VisitModalDocumentsTabProps {
  documents: DocumentFileResponseDto[];
  canEdit: boolean;
  visitId: string;
  patientId: string;
  onActivityChange: () => void;
  onDelete: (activityId: string) => void;
  getActivityIdForDocument: (filename: string) => string | undefined;
}

export default function VisitModalDocumentsTab({
  documents,
  canEdit,
  visitId,
  patientId,
  onActivityChange,
  onDelete,
  getActivityIdForDocument,
}: VisitModalDocumentsTabProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { isProfessionalRoleSelected } = useOrganizationContext();

  // TEXT_NOTE documents carry their content inline (no stored file), so they
  // open in the same editor modal used to add them — editable while the visit's
  // edit window is open (`canEdit`), view-only afterwards.
  const [noteModal, setNoteModal] = useState<{
    filename: string;
    content: string;
  } | null>(null);

  const { mutateAsync: updateTextNote, isLoading: isUpdatingNote } = useMutation(
    {
      mutationFn: (vars: { filename: string; content: string }) =>
        visitApi.visitControllerUpdateVisitTextNote({
          visitId,
          filename: vars.filename,
          updateTextNoteDto: { content: vars.content },
        }),
      onSuccess: () => {
        onActivityChange();
        setNoteModal(null);
        enqueueSnackbar(
          translateKey('hardcoded.notatka_zostala_zaktualizowana'),
          { variant: 'success' },
        );
      },
      onError: (error: any) => {
        enqueueSnackbar(
          error?.response?.data?.message ||
            translateKey('hardcoded.wystapil_blad_podczas_zapisywania_notatki'),
          { variant: 'error' },
        );
      },
    },
  );

  // Adding documents now goes through the global "Dodaj do wizyty" menu; this
  // tab only opens / deletes existing ones.
  const { mutate: deleteByFilename, isLoading: isDeletingByFilename } =
    useMutation({
      mutationFn: (filename: string) =>
        visitScopedPrescribingApi.deleteDocument(visitId, filename),
      onSuccess: () => {
        onActivityChange();
        enqueueSnackbar(t('visit.visitCourseRemoveSuccess'), {
          variant: 'success',
        });
      },
      onError: () => {
        enqueueSnackbar(t('visit.visitCourseRemoveError'), {
          variant: 'error',
        });
      },
    });

  // Always resolve a fresh signed URL from the per-entity endpoint, ignoring the
  // stored doc.url. The stored url is a short-lived (60-min) signed URL, so
  // opening it directly breaks once it expires; the documents collection is the
  // source of truth and the endpoint re-signs on demand. We open a blank tab
  // synchronously (within the click gesture) to dodge popup blockers, then
  // redirect it to the resolved URL.
  const openDocument = async (doc: DocumentFileResponseDto) => {
    // Text notes (e.g. the visit "Karta wizyty" / lab records) have no stored
    // file — show their inline content in a viewer instead of fetching a URL.
    if (
      doc.documentType === DocumentFileResponseDtoDocumentTypeEnum.TextNote
    ) {
      setNoteModal({ filename: doc.filename, content: doc.content || '' });
      return;
    }
    const newTab = window.open('', '_blank');
    try {
      const signedUrl = await getFileUrl({
        kind: 'visitFile',
        visitId,
        filename: doc.filename,
      });
      if (newTab) {
        newTab.location.href = signedUrl;
      } else {
        window.open(signedUrl, '_blank');
      }
    } catch {
      newTab?.close();
      enqueueSnackbar(
        translateKey('hardcoded.nie_udalo_sie_otworzyc_dokumentu'),
        { variant: 'error' },
      );
    }
  };

  if (documents.length === 0 && !canEdit) {
    return <VisitModalEmptyState message={translateKey("hardcoded.brak_dokumentow_przypisanych_podczas_tej_wizyty")} />;
  }

  return (
    <Stack sx={{ gap: 2, flex: 1, minHeight: 0 }}>
      {documents.length === 0 ? (
        <VisitModalEmptyState message={translateKey("hardcoded.brak_dokumentow_przypisanych_podczas_tej_wizyty")} />
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nazwa pliku</TableCell>
                <TableCell>{translateKey("search.ui.aboutTab")}</TableCell>
                <TableCell>Data dodania</TableCell>
                {canEdit && <TableCell align="right" />}
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((doc) => {
                const displayName =
                  doc.originalFilename ||
                  doc.filename?.split('_').slice(2).join('_') ||
                  doc.filename;
                const activityId = getActivityIdForDocument(doc.filename);
                const description = doc.description;
                const isTextNote =
                  doc.documentType ===
                  DocumentFileResponseDtoDocumentTypeEnum.TextNote;

                return (
                  <TableRow
                    key={doc.filename}
                    hover
                    onClick={() => openDocument(doc)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Stack
                        sx={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 1.5,
                        }}
                      >
                        {isTextNote ? (
                          <Iconify
                            icon="eva:file-text-outline"
                            width="24px"
                            color="primary.main"
                          />
                        ) : (
                          getFileIcon(displayName)
                        )}
                        <Typography variant="body2" fontWeight={500}>
                          {displayName}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={description || ''}
                        arrow
                        disableHoverListener={
                          !description || description.length < 30
                        }
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {description || ''}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {doc.uploadedAt
                          ? format(
                              new Date(doc.uploadedAt),
                              'dd.MM.yyyy HH:mm',
                            )
                          : '-'}
                      </Typography>
                    </TableCell>
                    {canEdit && (
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Prefer the activity-cascade path when we have
                            // a matching activity; fall back to the direct
                            // delete-by-filename endpoint for legacy docs
                            // (e.g. summary PDFs generated before activity
                            // logging was added).
                            if (activityId) {
                              onDelete(activityId);
                            } else {
                              deleteByFilename(doc.filename);
                            }
                          }}
                          disabled={isDeletingByFilename}
                          sx={{
                            color: theme.palette.text.disabled,
                            '&:hover': {
                              color: theme.palette.error.main,
                            },
                          }}
                        >
                          <Iconify
                            icon="eva:trash-2-outline"
                            width={18}
                          />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PatientNoteModal
        isOpen={!!noteModal}
        onClose={() => setNoteModal(null)}
        mode="edit"
        initialContent={noteModal?.content || ''}
        profileId={patientId}
        onSubmit={async (content) => {
          if (noteModal) {
            await updateTextNote({ filename: noteModal.filename, content });
          }
        }}
        isSaving={isUpdatingNote}
        // The visit modal never shows the assistant in the note editor (matches
        // the add-note flow); past the 30-day edit window it's view-only.
        canUseAssistant={false}
        enableDictation={canEdit && !!isProfessionalRoleSelected}
        readOnly={!canEdit}
      />
    </Stack>
  );
}
